import { useContext, useMemo } from "react";
import { FAILURE_TYPES_CODE_EXCEPTIONS } from "constants/constants";
import { UserContext } from "contexts/UserContext/UserContext";
import { IEventReportDetails } from "entities/EventReports/EventReportsEntityInterfaces";
import { DetailsReportEditionContext } from "contexts/DetailsReportEditionContext/DetailsReportEditionContext";
import { FailureTypeField } from "../SegmentFields";

/**
 * Inline-edit adapter: binds the controlled FailureTypeField to the
 * DetailsReportEditionContext for the table's row edit.
 */
export const FailureTypeDetailsDropdown = ({ row }: { row: IEventReportDetails }) => {
    const { user_id } = useContext(UserContext);
    const { edit, setEdit } = useContext(DetailsReportEditionContext);

    const existingEdition = useMemo(() => edit.findIndex((event) => event.id === row.event_report_id), [edit, row]);
    const selectedFailureTypeIdIsNotSelected = useMemo(
        () =>
            row.failure_type.failure_type_id === edit[existingEdition]?.changes?.failure_type_id &&
            row.failure_type.code === FAILURE_TYPES_CODE_EXCEPTIONS.NOT_SELECTED,
        [row, existingEdition, edit],
    );
    const selectedFailureTypeId = useMemo(
        () => (selectedFailureTypeIdIsNotSelected ? undefined : edit[existingEdition]?.changes?.failure_type_id),
        [existingEdition, edit, selectedFailureTypeIdIsNotSelected],
    );

    const handleFailureTypeUpdate = (id: number, isLocationSpecific: boolean) => {
        if (existingEdition === -1) return;
        setEdit((prevEdit) => {
            const newEdit = [...prevEdit];
            newEdit[existingEdition] = {
                ...newEdit[existingEdition],
                location_specific: isLocationSpecific,
                changes: {
                    ...newEdit[existingEdition]?.changes,
                    failure_type_id: id,
                    area_id: undefined,
                    functional_location_id: undefined,
                    failure_code_id: undefined,
                    ...(user_id && { edited_by_user_id: user_id }),
                },
            };
            return newEdit;
        });
    };

    return (
        <FailureTypeField
            machineCenterId={row.machine_center.machine_center_id}
            value={selectedFailureTypeId}
            obligatory={!selectedFailureTypeIdIsNotSelected}
            onChange={handleFailureTypeUpdate}
        />
    );
};
